import React, { useEffect, useRef, useState } from 'react'
import axios, { CancelTokenSource } from 'axios'
import {
  RegisterOptions,
  useFormContext,
  UseFormRegister
} from 'react-hook-form'
import api from '../../../services/api'
import {
  ArrowIcon,
  DropdownItem,
  DropdownList,
  NoOptionsMessage,
  SelectSearchContainer
} from '../styles'

const MIN_SEARCH_LENGTH = 5
const DEBOUNCE_MS = 500

export type SelectSearchAsyncProps = {
  register?: UseFormRegister<any>
  name: string
  label?: string
  className?: string
  rules?: RegisterOptions
  errors?: any
  placeholder?: string
  noOptionsMessage?: string
  defaultOption?: SelectOptions | null
}

export function SelectSearchAsync({
  register,
  name,
  label,
  className,
  rules,
  errors,
  placeholder = 'Digite para buscar...',
  noOptionsMessage = 'Nenhuma opção encontrada',
  defaultOption
}: SelectSearchAsyncProps): JSX.Element {
  const methods = useFormContext()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [options, setOptions] = useState<SelectOptions[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedOption, setSelectedOption] = useState<SelectOptions | null>(
    defaultOption || null
  )
  const selectRef = useRef<HTMLDivElement>(null)
  const cancelSourceRef = useRef<CancelTokenSource>()

  useEffect(() => {
    if (!defaultOption) {
      return
    }
    setSelectedOption(defaultOption)
    methods.setValue(name, defaultOption.value)
  }, [defaultOption, methods, name])

  const currentValue = methods.watch(name)

  useEffect(() => {
    if (!currentValue || selectedOption || defaultOption) {
      return
    }

    let cancelled = false

    const loadSelected = async () => {
      try {
        const response = await api.get(
          `/commercial/clients/view/${currentValue}`
        )
        if (cancelled) {
          return
        }
        const client = response.data as ClientsApiResponse
        setSelectedOption({
          value: client.id,
          name: `${client.cpf} - ${client.name}`
        })
      } catch (error) {}
    }

    loadSelected()

    return () => {
      cancelled = true
    }
  }, [currentValue, defaultOption, selectedOption])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (searchTerm.length < MIN_SEARCH_LENGTH) {
      cancelSourceRef.current?.cancel()
      setOptions([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    const timeout = window.setTimeout(async () => {
      cancelSourceRef.current?.cancel()
      const source = axios.CancelToken.source()
      cancelSourceRef.current = source

      try {
        const response = await api.get('/commercial/indicators/search', {
          params: { keyword: searchTerm },
          cancelToken: source.token
        })
        setOptions(
          (response.data || []).map((item: { id: number; name: string }) => ({
            name: item.name,
            value: item.id
          }))
        )
      } catch (error) {
        if (axios.isCancel(error)) {
          return
        }
        setOptions([])
      } finally {
        setIsSearching(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeout)
      cancelSourceRef.current?.cancel()
    }
  }, [searchTerm])

  const handleSelectOption = (option: SelectOptions) => {
    setSelectedOption(option)
    setSearchTerm('')
    setIsOpen(false)
    methods.setValue(name, option.value)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setIsOpen(true)
    if (!e.target.value) {
      setSelectedOption(null)
      methods.setValue(name, '')
    }
  }

  const handleInputFocus = () => {
    setIsOpen(true)
  }

  const keys = name.split('.')
  let error = keys.length === 2 ? errors?.[keys[0]]?.[keys[1]] : errors?.[name]
  error = keys.length === 3 ? errors?.[keys[0]]?.[keys[1]]?.[keys[2]] : error
  error =
    keys.length === 4
      ? errors?.[keys[0]]?.[keys[1]]?.[keys[2]]?.[keys[3]]
      : error
  error =
    keys.length === 5
      ? errors?.[keys[0]]?.[keys[1]]?.[keys[2]]?.[keys[3]]?.[keys[4]]
      : error

  const displayValue = selectedOption ? String(selectedOption.name) : ''

  const dropdownMessage =
    searchTerm.length > 0 && searchTerm.length < MIN_SEARCH_LENGTH
      ? 'Digite pelo menos 5 caracteres'
      : isSearching
      ? 'Buscando...'
      : noOptionsMessage

  return (
    <SelectSearchContainer ref={selectRef} erro={error} className={className}>
      {label && (
        <label htmlFor={name} className="col-form-label fw-bold fs-6">
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          className="form-control form-control-solid"
          placeholder={placeholder}
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          autoComplete="off"
        />
        <ArrowIcon isOpen={isOpen}>
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path
              d="M1 1L6 6L11 1"
              stroke="#5E6278"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </ArrowIcon>

        {isOpen && (
          <DropdownList>
            {options.length > 0 ? (
              options.map(option => (
                <DropdownItem
                  key={String(option.value)}
                  onClick={() => handleSelectOption(option)}
                  disabled={option.disabled}
                >
                  {option.name}
                </DropdownItem>
              ))
            ) : (
              <NoOptionsMessage>{dropdownMessage}</NoOptionsMessage>
            )}
          </DropdownList>
        )}

        <input
          type="hidden"
          name={name}
          {...(register && register(name, rules))}
        />
      </div>
    </SelectSearchContainer>
  )
}
